import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { listProjects, updateProject } from '../../api/projects';
import { advanceWorkflow, rejectWorkflow, getWorkflowHistory } from '../../api/workflow';
import type { ApprovalRecord } from '../../api/workflow';
import { toast } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { hasMinRole } from '../../utils/rbac';
import { fmt } from '../../utils/format';
import type { GrandProjet } from '../../types/estimation';
import { useTranslation } from 'react-i18next';
import { InfoTip } from '../shared/InfoTip';
import styles from './WorkflowPage.module.css';

const STAGES = [
  { key: 'draft', color: '#22d3ee' },
  { key: 'review', color: '#f39c12' },
  { key: 'approved', color: '#6366f1' },
  { key: 'construction', color: '#9b59b6' },
  { key: 'completed', color: '#2ecc71' },
] as const;

const STAGE_KEYS: Record<string, string> = {
  draft: 'draft',
  review: 'review',
  approved: 'approved',
  construction: 'construction',
  completed: 'completed',
};

// Required role to advance TO each stage
const STAGE_REQUIRED_ROLE: Record<string, string> = {
  review: 'analyst',
  approved: 'tenant_admin',
  construction: 'tenant_admin',
  completed: 'tenant_admin',
};

type Stage = typeof STAGES[number]['key'];

function normalizeStatus(status: string): Stage {
  const s = status.toLowerCase();
  if (s === 'active' || s === 'draft') return 'draft';
  if (s === 'review' || s === 'under review') return 'review';
  if (s === 'approved') return 'approved';
  if (s === 'construction' || s === 'in construction') return 'construction';
  if (s === 'completed' || s === 'done') return 'completed';
  return 'draft';
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('fr-CH', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export function WorkflowPage() {
  const { t } = useTranslation(['workflow', 'common']);
  const navigate = useNavigate();
  const [projects, setProjects] = useState<GrandProjet[]>([]);
  const [loading, setLoading] = useState(true);

  // Approval modal state
  const [approvalTarget, setApprovalTarget] = useState<{ projectId: string; projectName: string; action: 'advance' | 'reject' } | null>(null);
  const [approvalComment, setApprovalComment] = useState('');
  const [approvalLoading, setApprovalLoading] = useState(false);

  // History panel
  const [historyProjectId, setHistoryProjectId] = useState<string | null>(null);
  const [historyRecords, setHistoryRecords] = useState<ApprovalRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = currentUser ? hasMinRole(currentUser.role, 'tenant_admin') : false;
  const isAnalyst = currentUser ? hasMinRole(currentUser.role, 'analyst') : false;

  const tStage = (key: string) => t(`workflow:${STAGE_KEYS[key] ?? key}`);

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listProjects({ is_custom: true });
      setProjects(data);
    } catch {
      toast.error(t('common:failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Group projects by stage
  const columns = useMemo(() => {
    const map: Record<string, GrandProjet[]> = {};
    for (const s of STAGES) map[s.key] = [];
    for (const p of projects) {
      const stage = normalizeStatus(p.status);
      if (!map[stage]) map[stage] = [];
      map[stage]!.push(p);
    }
    return map;
  }, [projects]);

  function canUserAdvance(nextStage: string): boolean {
    const required = STAGE_REQUIRED_ROLE[nextStage];
    if (!required || !currentUser) return false;
    return hasMinRole(currentUser.role, required);
  }

  function canUserReject(): boolean {
    return isAnalyst;
  }

  // Open approval confirmation dialog
  function openApprovalDialog(projectId: string, projectName: string, action: 'advance' | 'reject') {
    setApprovalTarget({ projectId, projectName, action });
    setApprovalComment('');
  }

  // Execute the approval/rejection
  async function executeApproval() {
    if (!approvalTarget) return;
    if (approvalTarget.action === 'reject' && !approvalComment.trim()) {
      toast.error(t('workflow:rejectCommentRequired', 'A comment is required for rejections'));
      return;
    }
    setApprovalLoading(true);
    try {
      if (approvalTarget.action === 'advance') {
        await advanceWorkflow(approvalTarget.projectId, approvalComment || undefined);
        toast.success(t('workflow:advancedSuccess', 'Project advanced successfully'));
      } else {
        await rejectWorkflow(approvalTarget.projectId, approvalComment);
        toast.success(t('workflow:rejectedSuccess', 'Project sent back to previous stage'));
      }
      setApprovalTarget(null);
      await loadProjects();
    } catch {
      toast.error(t('common:failedToUpdate'));
    } finally {
      setApprovalLoading(false);
    }
  }

  // Fallback: direct move (for stages where workflow API isn't available yet)
  async function moveProject(projectId: string, newStage: Stage) {
    try {
      await updateProject(projectId, {
        status: newStage,
        change_summary: t('workflow:movedTo', { stage: tStage(newStage) }),
      } as never);
      toast.success(t('common:projectUpdated'));
      await loadProjects();
    } catch {
      toast.error(t('common:failedToUpdate'));
    }
  }

  // Load approval history for a project
  async function toggleHistory(projectId: string) {
    if (historyProjectId === projectId) {
      setHistoryProjectId(null);
      return;
    }
    setHistoryProjectId(projectId);
    setHistoryLoading(true);
    try {
      const records = await getWorkflowHistory(projectId);
      setHistoryRecords(records);
    } catch {
      setHistoryRecords([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  // Stage stats
  const stageStats = useMemo(() =>
    STAGES.map((s) => ({
      ...s,
      count: columns[s.key]?.length ?? 0,
      logements: (columns[s.key] ?? []).reduce((sum, p) => sum + p.logements, 0),
    })),
    [columns],
  );

  // Count pending approvals
  const pendingCount = columns['review']?.length ?? 0;

  if (loading) return <div className={styles.loading}>{t('common:loading')}</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h2>{t('workflow:title')}<InfoTip text={t('workflow:tooltips.board')} /></h2>
          <div className={styles.headerSub}>
            {t('workflow:subtitle')}
          </div>
        </div>
        {isAdmin && pendingCount > 0 && (
          <div style={{
            padding: '8px 16px', borderRadius: 8,
            background: 'rgba(243, 156, 18, 0.1)',
            border: '1px solid rgba(243, 156, 18, 0.3)',
            color: '#f39c12', fontSize: 13, fontWeight: 600,
          }}>
            {pendingCount} {t('workflow:pendingApproval', 'pending approval')}
          </div>
        )}
      </div>

      {/* Stage summary */}
      <div className={styles.statsRow}>
        {stageStats.map((s) => (
          <div key={s.key} className={styles.statCard}>
            <div className={styles.statValue} style={{ color: s.color }}>{s.count}</div>
            <div className={styles.statLabel}>{tStage(s.key)}</div>
          </div>
        ))}
      </div>

      {/* Kanban Board */}
      <div className={styles.board}>
        {STAGES.map((stage) => {
          const stageIdx = STAGES.findIndex((s) => s.key === stage.key);
          const stageProjects = columns[stage.key] ?? [];
          const nextStage = stageIdx < STAGES.length - 1 ? STAGES[stageIdx + 1]!.key : null;
          const prevStage = stageIdx > 0 ? STAGES[stageIdx - 1]!.key : null;

          return (
            <div key={stage.key} className={styles.column}>
              <div className={styles.columnHeader}>
                <div className={styles.columnTitle}>
                  <span className={styles.columnDot} style={{ background: stage.color }} />
                  {tStage(stage.key)}
                  {/* Show required role badge */}
                  {STAGE_REQUIRED_ROLE[stage.key] && (
                    <span style={{
                      fontSize: 9, padding: '1px 6px', borderRadius: 3,
                      background: 'rgba(255,255,255,0.06)', color: 'var(--color-text-muted)',
                      textTransform: 'uppercase', letterSpacing: '0.03em',
                    }}>
                      {STAGE_REQUIRED_ROLE[stage.key]}
                    </span>
                  )}
                </div>
                <span className={styles.columnCount}>{stageProjects.length}</span>
              </div>
              <div className={styles.columnBody}>
                {stageProjects.map((p) => {
                  const showingHistory = historyProjectId === p.id;
                  return (
                    <div key={p.id} className={styles.card}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/app/projects/${p.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/app/projects/${p.id}`);
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className={styles.cardTitle}>{p.name}</div>
                        <div className={styles.cardMeta}>
                          <span>{p.logements} {t('workflow:log')}</span>
                          <span>{p.emplois} {t('workflow:emp')}</span>
                          {p.ha_total != null && <span>{p.ha_total} ha</span>}
                        </div>
                        <div className={styles.cardMeta}>
                          <span>{p.communes.slice(0, 2).join(', ')}</span>
                        </div>
                      </div>

                      {/* Approval actions */}
                      <div className={styles.cardActions} onClick={(e) => e.stopPropagation()}>
                        {/* Reject button (send back) */}
                        {prevStage && canUserReject() && stage.key !== 'draft' && (
                          <button
                            className={styles.moveBtn}
                            style={{ borderColor: 'rgba(231, 76, 60, 0.3)', color: '#e74c3c' }}
                            onClick={() => openApprovalDialog(p.id, p.name, 'reject')}
                            title={t('workflow:rejectTooltip', 'Reject and send back')}
                          >
                            &larr; {t('workflow:reject', 'Reject')}
                          </button>
                        )}

                        {/* History toggle */}
                        <button
                          className={styles.moveBtn}
                          style={showingHistory ? { borderColor: 'var(--color-primary)', color: 'var(--color-primary)' } : {}}
                          onClick={() => toggleHistory(p.id)}
                          title={t('workflow:historyTooltip', 'Approval history')}
                        >
                          {t('workflow:history', 'History')}
                        </button>

                        {/* Advance button */}
                        {nextStage && canUserAdvance(nextStage) && (
                          <button
                            className={styles.moveBtn}
                            style={{ borderColor: 'rgba(99, 102, 241, 0.3)', color: 'var(--color-primary)', fontWeight: 600 }}
                            onClick={() => openApprovalDialog(p.id, p.name, 'advance')}
                            title={t('workflow:approveTooltip', 'Approve and advance')}
                          >
                            {tStage(nextStage)} &rarr;
                          </button>
                        )}

                        {/* Fallback move for users without approval workflow endpoint */}
                        {nextStage && !canUserAdvance(nextStage) && isAnalyst && stage.key === 'draft' && (
                          <button
                            className={styles.moveBtn}
                            onClick={() => moveProject(p.id, nextStage as Stage)}
                          >
                            {tStage(nextStage)} &rarr;
                          </button>
                        )}
                      </div>

                      {/* Inline approval history */}
                      {showingHistory && (
                        <div style={{
                          marginTop: 8, padding: 8,
                          background: 'rgba(0,0,0,0.15)',
                          borderRadius: 6, fontSize: 11,
                        }}>
                          {historyLoading ? (
                            <div style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 8 }}>
                              {t('common:loading')}
                            </div>
                          ) : historyRecords.length === 0 ? (
                            <div style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 8 }}>
                              {t('workflow:noHistory', 'No approval history yet')}
                            </div>
                          ) : (
                            historyRecords.slice(0, 5).map((rec) => (
                              <div key={rec.id} style={{
                                padding: '4px 0',
                                borderBottom: '1px solid rgba(255,255,255,0.04)',
                                display: 'flex', alignItems: 'flex-start', gap: 6,
                              }}>
                                <span style={{
                                  fontSize: 9, padding: '1px 5px', borderRadius: 3, flexShrink: 0,
                                  background: rec.action === 'approve' ? 'rgba(99,102,241,0.15)' : 'rgba(231,76,60,0.15)',
                                  color: rec.action === 'approve' ? '#6366f1' : '#e74c3c',
                                  fontWeight: 600, textTransform: 'uppercase',
                                }}>
                                  {rec.action === 'approve' ? t('workflow:approved', 'Approved') : t('workflow:rejected', 'Rejected')}
                                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ color: 'var(--color-text)' }}>
                                    {rec.from_stage} &rarr; {rec.to_stage}
                                  </div>
                                  {rec.comment && (
                                    <div style={{ color: 'var(--color-text-muted)', fontStyle: 'italic', marginTop: 2 }}>
                                      &ldquo;{rec.comment}&rdquo;
                                    </div>
                                  )}
                                  <div style={{ color: 'var(--color-text-muted)', fontSize: 10, marginTop: 2 }}>
                                    {rec.user_name ?? 'Unknown'} ({rec.user_role}) &middot; {formatDate(rec.created_at)}
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {stageProjects.length === 0 && (
                  <div style={{ textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 12, padding: 20 }}>
                    {t('workflow:noProjects')}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pipeline summary */}
      <div className={styles.timelineSection}>
        <h3 className={styles.sectionTitle}>{t('workflow:pipelineSummary')}</h3>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12, marginTop: 12,
        }}>
          {stageStats.map((s) => (
            <div key={s.key} style={{
              padding: 14,
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 8,
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
                fontSize: 12, fontWeight: 600,
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color }} />
                {tStage(s.key)}
              </div>
              <div style={{ fontSize: 13 }}>
                <span style={{ fontFamily: 'var(--font-heading)' }}>{s.count}</span> {t('workflow:projects')}
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                {fmt(s.logements)} {t('workflow:logementsTotal')}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Approval confirmation modal */}
      {approvalTarget && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => !approvalLoading && setApprovalTarget(null)}>
          <div style={{
            padding: 24, borderRadius: 12, maxWidth: 440, width: '90%',
            background: 'var(--color-surface, #1a1a2e)',
            border: '1px solid rgba(255,255,255,0.1)',
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{
              fontFamily: 'var(--font-heading)', fontSize: 18, marginBottom: 8,
              color: approvalTarget.action === 'advance' ? 'var(--color-primary)' : '#e74c3c',
            }}>
              {approvalTarget.action === 'advance'
                ? t('workflow:confirmAdvance', 'Confirm Advancement')
                : t('workflow:confirmReject', 'Confirm Rejection')
              }
            </h3>
            <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 16 }}>
              {approvalTarget.action === 'advance'
                ? t('workflow:confirmAdvanceDesc', { name: approvalTarget.projectName })
                : t('workflow:confirmRejectDesc', { name: approvalTarget.projectName })
              }
            </p>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                {approvalTarget.action === 'reject'
                  ? t('workflow:rejectReason', 'Reason for rejection *')
                  : t('workflow:commentOptional', 'Comment (optional)')
                }
              </label>
              <textarea
                value={approvalComment}
                onChange={(e) => setApprovalComment(e.target.value)}
                placeholder={approvalTarget.action === 'reject'
                  ? t('workflow:rejectPlaceholder', 'Explain why the project is being sent back...')
                  : t('workflow:commentPlaceholder', 'Add a note about this decision...')
                }
                rows={3}
                style={{
                  width: '100%', padding: '8px 10px', fontSize: 13,
                  border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6,
                  background: 'rgba(0,0,0,0.2)', color: 'var(--color-text)',
                  resize: 'vertical', boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setApprovalTarget(null)}
                disabled={approvalLoading}
                style={{
                  padding: '6px 16px', fontSize: 13,
                  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6,
                  background: 'transparent', color: 'var(--color-text)', cursor: 'pointer',
                }}
              >
                {t('common:cancel', 'Cancel')}
              </button>
              <button
                onClick={executeApproval}
                disabled={approvalLoading || (approvalTarget.action === 'reject' && !approvalComment.trim())}
                style={{
                  padding: '6px 16px', fontSize: 13, fontWeight: 600,
                  border: 'none', borderRadius: 6, cursor: 'pointer',
                  background: approvalTarget.action === 'advance' ? 'var(--color-primary)' : '#e74c3c',
                  color: approvalTarget.action === 'advance' ? '#000' : '#fff',
                  opacity: approvalLoading ? 0.5 : 1,
                }}
              >
                {approvalLoading
                  ? t('common:loading')
                  : approvalTarget.action === 'advance'
                    ? t('workflow:approve', 'Approve')
                    : t('workflow:rejectBtn', 'Reject')
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
